import { useMantineTheme } from '@mantine/core'
import { useDebouncedValue, usePrevious } from '@mantine/hooks'
import { StatusBarArea } from 'components/StatusBarArea/StatusBarArea'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore, useScreenOrientation } from 'stores/smallStore/smallStore'
import { MapContext } from '../MapContext/MapContext'
import { getMapPanForSizeChange } from './_utils/getMapPanForSizeChange'
import { OverviewCharts } from './OverviewCharts/OverviewCharts'
import { BiofixModalHost } from './SectionTitleBars/InsectTrapAndDegreeTitleLink/BiofixModalHost'
import { StackemCharts } from './StackemCharts/StackemCharts'
import { SummaryGridTable } from './SummaryGridTable/SummaryGridTable'

export const PanelDetailsContext = createContext<{ containerWidth: number }>({ containerWidth: 0 })

export const PanelDetails = ({
  panelWidth,
  panelHeight,
  isPanelExpanded,
}: {
  panelWidth: number
  panelHeight: number
  isPanelExpanded: boolean
}) => {
  const [containerWidth] = useDebouncedValue(panelWidth, 125)
  const [containerHeight] = useDebouncedValue(panelHeight, 125)
  const safeAreaTop = smallStore.useSelector((s) => s.safeArea.insets.top)
  const statusBarAreaHeight = Math.max(safeAreaTop - 5, 0)
  const tabHeight = containerHeight - statusBarAreaHeight
  const theme = useMantineTheme()
  const { map } = useContext(MapContext)
  const currentSelectedTab = detailsPanelStore.useSelector((s) => s.selectedTab)
  const selectedValueGroups = selectedValueGroupsStore.useSelector((s) => s.selectedValueGroups)
  const oldContainerWidth = usePrevious(containerWidth)
  // TODO: The container height isnt changing when panel is opened on non widescreen
  const oldContainerHeight = usePrevious(containerHeight)
  const isLandscape = useScreenOrientation() === 'landscape'
  const orientationAtLastSizeChange = useRef({ width: isLandscape, height: isLandscape })
  const [refreshOverviewKey, setRefreshOverviewKey] = useState(0)

  useEffect(() => {
    const { pan, orientationAtLastChange } = getMapPanForSizeChange({
      width: containerWidth,
      oldWidth: oldContainerWidth,
      height: containerHeight,
      oldHeight: oldContainerHeight,
      isLandscape,
      orientationAtLastChange: orientationAtLastSizeChange.current,
    })

    orientationAtLastSizeChange.current = orientationAtLastChange

    if (pan) map?.panBy(pan.x, pan.y)
  }, [containerWidth, containerHeight])

  /*
    On first render, when no tab is selected yet, and the user drag the panel, we don't want to show an empty panel.
    Instead, we show the summary page.
   **/
  useEffect(() => {
    if (isPanelExpanded && currentSelectedTab === '') {
      detailsPanelStore.setState((s) => ({ ...s, selectedTab: 'OVERVIEW' }))
    }
  }, [isPanelExpanded])

  useEffect(() => {
    if (currentSelectedTab === 'OVERVIEW') {
      setRefreshOverviewKey((prev) => prev + 1)
    }
  }, [currentSelectedTab])

  return (
    <PanelDetailsContext.Provider value={{ containerWidth }}>
      <StatusBarArea backgroundColor={theme.colors.midnight[0]} darkContent={true} />
      <div css={{ display: currentSelectedTab === 'OVERVIEW' ? 'block' : 'none' }}>
        <OverviewCharts
          refreshKey={refreshOverviewKey}
          preventFetch={!isPanelExpanded || currentSelectedTab !== 'OVERVIEW'}
          containerHeight={tabHeight}
          containerWidth={containerWidth}
          selectedValueGroups={selectedValueGroups}
        />
      </div>
      <div css={{ display: currentSelectedTab === 'GRAPHS' ? 'block' : 'none' }}>
        <div
          css={{
            width: containerWidth,
            height: tabHeight,
            maxHeight: tabHeight,
            overflow: 'auto',
            userSelect: 'none',
          }}
        >
          <StackemCharts
            preventFetch={!isPanelExpanded || currentSelectedTab !== 'GRAPHS'}
            containerWidth={containerWidth}
            selectedValueGroups={selectedValueGroups}
          />
        </div>
      </div>
      <div css={{ display: currentSelectedTab === 'TABLES' ? 'block' : 'none' }}>
        <SummaryGridTable
          preventFetch={!isPanelExpanded || currentSelectedTab !== 'TABLES'}
          containerHeight={tabHeight}
          containerWidth={containerWidth}
          selectedValueGroups={selectedValueGroups}
        />
      </div>
      <BiofixModalHost />
    </PanelDetailsContext.Provider>
  )
}
